import { formatDistanceToNow } from 'date-fns'
import React from 'react'
import { formatPrice, formatTime } from '../../GuestOrder/utils/formatters'

export function OrderCard({ order, onUpdateStatus, buttonText, buttonColor, showUndo, undoText, onUndo, showActions = true, className = '', isPending = false, glow = false }) {
  const timeAgo = formatDistanceToNow(new Date(order.created_at), { addSuffix: true })

  const glowClass = glow
    ? 'shadow-sm border-2 border-red-400 animate-[glow_2s_ease-in-out_infinite]'
    : (className || 'shadow-sm border border-gray-200')

  return (
    <div className={`bg-white rounded-lg p-4 relative ${glowClass}`}>
      {/* Loading overlay for pending orders */}
      {isPending && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center rounded-lg z-10">
          <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      )}

      {/* Order Header */}
      <div className="flex justify-between items-start mb-3">
        <div>
          {order.order_code && (
            <div className="font-bold text-lg text-gray-900">{order.order_code}</div>
          )}
          <div className={`text-gray-600 ${order.order_code ? 'text-xs font-mono' : 'font-bold text-lg text-gray-900'}`}>#{order.order_number}</div>
          <div className="text-sm text-gray-600">{timeAgo}</div>
          <div className="text-sm font-medium text-gray-900">
            {formatPrice(order.total_pence)}
          </div>
        </div>
        {/* Prominent pickup time / table badge */}
        {order.table_number ? (
          <div className="bg-emerald-50 border border-emerald-200 px-4 py-2 rounded-lg text-center">
            <div className="text-2xl font-bold text-emerald-800">Table {order.table_number}</div>
            <div className="text-xs uppercase tracking-wide font-semibold text-emerald-600">Dine In</div>
          </div>
        ) : order.dining_option === 'takeaway' ? (
          <div className="bg-amber-50 border border-amber-200 px-4 py-2 rounded-lg text-center">
            <div className="text-2xl font-bold text-amber-800 flex items-center justify-center gap-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {formatTime(order.pickup_time)}
            </div>
            <div className="text-xs uppercase tracking-wide font-semibold text-amber-600">Takeaway</div>
          </div>
        ) : (
          <div className="bg-sky-50 border border-sky-200 px-4 py-2 rounded-lg text-center">
            <div className="text-2xl font-bold text-sky-900 flex items-center justify-center gap-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              {formatTime(order.pickup_time)}
            </div>
            <div className="text-xs uppercase tracking-wide font-semibold text-sky-600">Sit In</div>
          </div>
        )}
      </div>

      {/* Order Items */}
      <div className={`space-y-2 ${showActions || order.customer_notes ? 'mb-4' : ''}`}>
        {order.order_items.map(item => (
          <div key={item.id} className="flex justify-between text-sm">
            <span className="text-gray-900">
              {item.quantity}x {item.menu_item.name}
              {item.add_ons && item.add_ons.length > 0 && (
                <span className="block ml-4 font-semibold text-emerald-700">
                  {item.add_ons.map(a => `+ ${a.name}`).join(', ')}
                </span>
              )}
            </span>
            <span className={`text-xs px-2 py-1 rounded capitalize h-fit ${
              item.menu_item.category === 'food'
                ? 'bg-orange-100 text-orange-700'
                : 'bg-blue-100 text-blue-700'
            }`}>
              {item.menu_item.category}
            </span>
          </div>
        ))}
      </div>

      {/* Customer Notes — shown in every view, allergy info must never be hidden */}
      {order.customer_notes && (
        <div className="mb-3 p-3 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
          <div className="flex items-start gap-2">
            <span className="text-yellow-600 text-base">⚠️</span>
            <div className="flex-1">
              <div className="text-sm font-bold text-yellow-800 mb-1">Special Requests</div>
              <div className="text-base font-medium text-yellow-900 whitespace-pre-wrap">{order.customer_notes}</div>
            </div>
          </div>
        </div>
      )}

      {showActions && (
        <>
          {/* Contact Info */}
          <div className="text-xs text-gray-600 mb-3 border-t pt-3">
            <div>📧 {order.email}</div>
            <div>📱 {order.phone}</div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            {/* Main Action Button */}
            <button
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => onUpdateStatus(order.order_number)}
              className={`w-full text-white font-bold py-2 px-4 rounded-lg transition ${buttonColor}`}
            >
              {buttonText}
            </button>

            {/* Undo Button */}
            {showUndo && (
              <button
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => onUndo(order.order_number)}
                className="w-full text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium py-2 px-4 rounded-lg transition text-sm border border-gray-300"
              >
                {undoText}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}
